import {
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {
  DollarOutlined,
  FundProjectionScreenOutlined,
  HomeOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useLoadScript } from '@react-google-maps/api'
import type { GetProp, UploadFile, UploadProps } from 'antd'
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  TablePaginationConfig,
  TableProps,
  Tooltip,
  Typography,
  Upload,
} from 'antd'
import copy from 'copy-to-clipboard'
import type { Dayjs } from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Web3 from 'web3'

import abiLending from 'abi/abi-lending.json'
import { AppAdmin } from 'api/app-admin'
import countries from 'constants/country'
import { FEATURE } from 'constants/feature'
import { HISTORY_TYPE } from 'constants/history-type'
import { WEB3_PROVIDER } from 'constants/index'
import { usePermission } from 'hooks/use-permission'
import dayjs from 'libs/dayjs'
import { formatNumber } from 'utils/formatted-number'
import { logError } from 'utils/logger'

import './style.scss'

const { Title } = Typography

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface PropertyItem {
  id: number
  value: string
}

interface PropertyType {
  id: string
  name: string
  properties: { key: number; name: string }[]
}

interface TimeLine {
  time: string
  title: string
  desc: string
}

interface DocumentDetail {
  name: string
  link: string
}

interface FinancialDetail {
  name: string
  value: string
}

interface DocumentData {
  timeLines: TimeLine[]
  documentDetail: DocumentDetail[]
  financial: FinancialDetail[]
}

interface TransactionData {
  key: string
  date: string
  type: string
  address: string
  contract: string
  amount: string
  asset: string
  txHash: string
  status: number
}

export default function P2PDetail() {
  const locationParam = useParams()
  const [contractAddress, setContractAddress] = useState('')
  const code = locationParam.code || ''
  type DetailType = {
    _baseAPY: number
    _whitelistBoostAPY: number
    _endTime: number
    _startTime: number
    _fundingCap: number
    _totalLentAmount: number
    _totalLenders: number
    _fundingOpenTime: number
    _boostedAPY: string
  }

  const [data, setData] = useState<DetailType>({
    _baseAPY: 0,
    _whitelistBoostAPY: 0,
    _endTime: 0,
    _startTime: 0,
    _fundingCap: 0,
    _totalLentAmount: 0,
    _totalLenders: 0,
    _fundingOpenTime: 0,
    _boostedAPY: '',
  })
  const web3 = new Web3(WEB3_PROVIDER)
  const contract = new web3.eth.Contract(abiLending, contractAddress)
  const [loading, setLoading] = useState(false)
  const createContract = useCallback(async () => {
    try {
      setLoading(true)
      const result: any = await contract.methods.getContractInfo().call()
      setData({
        _baseAPY: result._baseAPY,
        _whitelistBoostAPY: result._whitelistBoostAPY,
        _endTime: result._endTime,
        _startTime: result._startTime,
        _fundingCap: result._fundingCap,
        _totalLentAmount: result._totalLentAmount,
        _totalLenders: result._totalLenders,
        _fundingOpenTime: result._fundingOpenTime,
        _boostedAPY: result._boostedAPYWhitelistContract,
      })
      setLoading(false)
    } catch (err) {
      setLoading(false)
      logError(err)
    }
  }, [contract.methods])

  useEffect(() => {
    createContract()
  }, [contractAddress, createContract])

  const [searchData, setSearchData] = useState<{ lenderTotalBalance?: any; lastClaimTime?: any }>({
    lenderTotalBalance: 0,
    lastClaimTime: 0,
  })
  const [notFound, setNotFound] = useState(true)
  const getSearchData = useCallback(async () => {
    try {
      const lastClaimTime = await contract.methods.lastClaimTime(address).call()
      const lenderTotalBalance = await contract.methods.lenderTotalBalance(address).call()
      setSearchData({
        lenderTotalBalance: Number(lenderTotalBalance),
        lastClaimTime: Number(lastClaimTime),
      })
      setNotFound(false)
    } catch (err) {
      setNotFound(true)
      logError(err)
    }
  }, [address, contract.methods])

  const detail = [
    {
      title: 'Total Lenders',
      value: Number(data._totalLenders),
      icon: (
        <UserOutlined
          className="icons-style"
          style={{ background: 'lightyellow', color: 'goldenrod' }}
        />
      ),
    },
    {
      title: 'Total Lent Amount',
      value:
        Number(data._totalLentAmount) === 0
          ? 0
          : formatNumber(web3.utils.fromWei(data._totalLentAmount, 'ether'), 3).toString() + ' MEY',
      icon: (
        <DollarOutlined
          className="icons-style"
          style={{ background: 'peachpuff', color: 'sienna' }}
        />
      ),
    },
    {
      title: 'Funding Cap',
      value:
        Number(data._fundingCap) === 0
          ? 0
          : formatNumber(web3.utils.fromWei(data._fundingCap, 'ether'), 3).toString() + ' MEY',
      icon: (
        <DollarOutlined
          className="icons-style"
          style={{ background: 'lightgreen', color: 'darkgreen' }}
        />
      ),
    },
    {
      title: 'Base APY',
      value: (Number(data._baseAPY) / 100).toString() + '%',
      icon: (
        <FundProjectionScreenOutlined
          className="icons-style"
          style={{ background: 'chartreuse', color: 'darkcyan' }}
        />
      ),
    },
  ]

  const [dataTable, setDataTable] = useState<TransactionData[]>([])
  const [total, setTotal] = useState(0)
  const [current, setCurrent] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [addressSearch, setAddressSearch] = useState('')
  const [type, setType] = useState(0)
  const [address] = useState<string>('')

  const getDataTable = useCallback(
    async (pageIndex = 1, pageSize = 10) => {
      try {
        const res = await AppAdmin.getListP2PTransaction(
          contractAddress,
          pageSize,
          pageIndex - 1,
          addressSearch,
          type
        )
        if (res?.data.success) {
          const transaction = res?.data.data.map((item: any, index: number) => ({
            key: `${pageIndex}-${index}`,
            date: item.date,
            type: item.type,
            address: item.address,
            contract: item.contract,
            amount: item.amount,
            asset: item.asset,
            txHash: item.txHash,
            status: item.status,
          }))
          setDataTable(transaction)
          setTotal(res?.data.totalCount)
        }
      } catch (error) {
        setDataTable([])
        setTotal(0)
        setNotFound(true)
        logError('Error fetching data: ', error)
      }
    },
    [addressSearch, contractAddress, type]
  )

  useEffect(() => {
    if (contractAddress) {
      getDataTable(current, pageSize)
    }
  }, [current, pageSize, addressSearch, type, contractAddress, getDataTable])

  useEffect(() => {
    setCurrent(1)
    setPageSize(10)
  }, [addressSearch, type])

  useEffect(() => {
    getSearchData()
  }, [addressSearch, getSearchData])

  const statusClassMap: any = {
    1: { text: 'Pending', className: 'status-pending' },
    2: { text: 'Success', className: 'status-success' },
    3: { text: 'Fail', className: 'status-fail' },
  }

  const columns: TableProps<TransactionData>['columns'] = [
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/address/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const { text, className } = statusClassMap[status] || {}
        return <span className={className}>{text || 'Unknown'}</span>
      },
    },
    {
      title: 'Tx Hash',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/tx/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
  ]

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || 1)
    setPageSize(newPageSize || 10)
  }

  const [form] = Form.useForm()
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [propertyFields, setPropertyFields] = useState<
    { key: number; name: string; value: string }[]
  >([])
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [singleFile, setSingleFile] = useState<UploadFile[]>([])
  const [defaultFields, setDefaultFields] = useState<
    { key: number; name: string; value: string }[]
  >([])
  const [edit, setEdit] = useState(false)

  const handleUploadSingle = async (info: any) => {
    const { file } = info
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await AppAdmin.uploadSingleFile(formData)
      if (response.data.success) {
        message.success('File uploaded successfully')
        setThumbnail(response.data.data.filePath)
        form.setFields([{ name: 'uploadSingle', errors: [] }])
      }
    } catch (error: any) {
      message.error(error?.message || 'File upload failed')
      logError('Upload error:', error)
    }
  }

  const handleUploadMulti = async () => {
    const formData = new FormData()
    fileList.forEach((file) => {
      formData.append('files', file as FileType)
    })
    try {
      const response = await AppAdmin.uploadMultipleFiles(formData)
      if (response.data.success) {
        message.success('Files uploaded successfully')
        const list = response.data.data.map((item: any) => item.filePath)
        const updatedList = [...thumbnails, ...list]
        setThumbnails(updatedList)
        form.setFields([{ name: 'uploadMulti', errors: [] }])
      }
    } catch (error: any) {
      message.error(error?.message || 'Files upload failed')
      logError('Upload error:', error)
    } finally {
      setFileList([])
    }
  }

  const beforeUploadSingle = async (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      form.setFields([{ name: 'uploadSingle', errors: ['You can only upload JPG/PNG file!'] }])
      return false
    }
    // const isLt2M = file.size / 1024 / 1024 < 2
    // if (!isLt2M) {
    //   form.setFields([
    //     { name: "uploadSingle", errors: ["Image must smaller than 2MB!"] },
    //   ])
    //   return false
    // }

    // const isCorrectDimensions = await new Promise((resolve) => {
    //   const img = new Image()
    //   const reader = new FileReader()

    //   reader.onload = (event) => {
    //     if (event.target && event.target.result) {
    //       img.src = event.target.result as string
    //     }
    //     img.onload = () => {
    //       const isValidSize = img.width === 858 && img.height === 423
    //       resolve(isValidSize)
    //     }
    //   }

    //   reader.readAsDataURL(file)
    // })

    // if (!isCorrectDimensions) {
    //   form.setFields([
    //     { name: "uploadSingle", errors: ["Image must be 858x423 pixels!"] },
    //   ])
    //   return false
    // }
    return true
  }

  const beforeUploadMulti = async (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      form.setFields([{ name: 'upLloadMulti', errors: ['You can only upload JPG/PNG file!'] }])
    } else {
      setFileList([...fileList, file])
    }

    return false
  }

  const props: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file)
      const newFileList = fileList.slice()
      newFileList.splice(index, 1)
      setFileList(newFileList)
    },
    fileList,
  }

  const handleRemoveImage = (indexToRemove: any) => {
    const updatedThumbnails = thumbnails.filter((_, index) => index !== indexToRemove)
    setThumbnails(updatedThumbnails) // Ensure this updates your thumbnails state
  }

  const handleGetP2PDetails = useCallback(async () => {
    try {
      const detailResponse = await AppAdmin.getp2pDetail(code)
      setShowInfo(false)
      if (detailResponse.data.success) {
        const details = detailResponse.data.data
        setContractAddress(details.contractAddress)
        const loc = { lat: details.latitude, lng: details.longitude }
        setCenter(loc)
        setLatitude(loc.lat)
        setLongitude(loc.lng)
        const propertyValues = details.propertyItems.reduce(
          (acc: Record<string, string>, item: PropertyItem) => {
            acc[`property_${item.id}`] = item.value
            return acc
          },
          {}
        )
        setSwitchChecked(details.p2pStatus === 1)
        const formValues = {
          code,
          name: details.fullName,
          type: details.type,
          country: details.country.toUpperCase(),
          contractAddress: details.contractAddress,
          apy: details.apy,
          description: details.description,
          investmentPeriod: details.investmentPeriod,
          latitude: details.latitude,
          longitude: details.longitude,
          status: details.p2pStatus === 1,
          ...propertyValues,
        }
        form.setFieldsValue(formValues)
        const res = await AppAdmin.getListProperty()
        let types = []
        if (res?.data.success) {
          types = res?.data.data.map((item: PropertyType) => ({
            ...item,
            id: Number(item.id),
          }))
          setPropertyTypes(types)
        }
        const selectedType = types.find((type: any) => type.id === details.type)
        if (selectedType) {
          const propertiesWithValues = selectedType.properties.map((prop: any) => {
            const matchedProperty = details.propertyItems.find(
              (item: PropertyItem) => item.id === prop.key
            )
            return {
              key: prop.key,
              name: prop.name,
              value: matchedProperty ? matchedProperty.value : '',
            }
          })

          setPropertyFields(propertiesWithValues)
          setDefaultFields(propertiesWithValues)
        }
        setThumbnail(details.thumbnail)
        setThumbnails(details.thumbnails)
        setShowLocation(true)
      }
    } catch (error) {
      setShowInfo(false)
      setShowLocation(false)
      logError('Error fetching details: ', error)
    }
  }, [code, form])

  const [, setIsApiLoaded] = useState(false)
  useEffect(() => {
    const timeoutId = setTimeout(() => setIsApiLoaded(true), 1000)

    return () => {
      clearTimeout(timeoutId) // Clear the timeout when the component unmounts
    }
  }, [])

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      const {
        code,
        type,
        name,
        country,
        investmentPeriod,
        contractAddress,
        description = '',
        latitude,
        longitude,
        status,
      } = values

      const propertyItems = propertyFields.map((item) => ({
        id: Number(item.key),
        value: item.value,
      }))

      // Call the p2pUpdate function from your API
      await AppAdmin.p2pUpdate({
        code: code,
        type: type,
        fullName: name,
        country: country,
        investmentPeriod: investmentPeriod,
        contractAddress: contractAddress,
        description: description,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        propertyItems: propertyItems,
        latitude: latitude,
        longitude: longitude,
        status: status,
      })
      message.success('P2P lending updated successfully!')
      handleGetP2PDetails()
      setEdit(false)
    } catch (error: any) {
      message.error(error?.message || 'Error while submitting form')
      logError('Error while submitting form:', error)
    }
  }

  const handleCancel = () => {
    getDocumentDetails()
    handleGetP2PDetails()
    setEdit(false)
  }

  const [, setShowLocation] = useState(false)

  const handlePropertyTypeChange = (value: string) => {
    const selectedType = propertyTypes.find((type) => type.id === value)
    if (selectedType) {
      setShowLocation(true)
      const propertiesWithValues = selectedType.properties.map((prop) => ({
        key: prop.key,
        name: prop.name,
        value: prop.name === 'Location' && address.length > 0 ? address : '',
      }))
      const updatedPropertiesWithValues = propertiesWithValues.map((prop) => {
        const field = defaultFields.find((field) => field.key === prop.key)
        if (field) {
          return { ...prop, value: field.value }
        }
        return prop
      })
      setPropertyFields(updatedPropertiesWithValues)
    } else {
      setShowLocation(false)
    }
  }

  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.P2P_LENDING)
  const [documentDetails, setDocumentDetails] = useState<DocumentData>({
    timeLines: [],
    documentDetail: [],
    financial: [],
  })
  const [formDocumentDetail] = Form.useForm()
  // Fetch and populate initial data
  const getDocumentDetails = useCallback(async () => {
    try {
      const res = await AppAdmin.getDocumentDetails(code)
      if (res?.data.success) {
        setDocumentDetails({
          documentDetail: res?.data.data.documentDetail,
          financial: res?.data.data.financial,
          timeLines: res?.data.data.timeLines,
        })
        formDocumentDetail.setFieldsValue(res?.data.data) // Set initial form values
      } else {
        setDocumentDetails({
          timeLines: [],
          documentDetail: [],
          financial: [],
        })
        formDocumentDetail.resetFields()
      }
    } catch (error) {
      setDocumentDetails({
        timeLines: [],
        documentDetail: [],
        financial: [],
      })
      formDocumentDetail.resetFields()
      logError('Error: ', error)
    }
  }, [code, formDocumentDetail])

  const handleFieldChange = <T extends keyof DocumentData>(
    field: T,
    index: number,
    key: keyof DocumentData[T][number],
    value: string
  ) => {
    setDocumentDetails((prevDetails) => {
      const updatedField = [...prevDetails[field]] // Remove type assertion
      updatedField[index] = { ...updatedField[index], [key]: value }
      return { ...prevDetails, [field]: updatedField }
    })
  }

  const handleDeleteItem = <T extends keyof DocumentData>(field: T, index: number) => {
    setDocumentDetails((prevDetails) => {
      // Create a shallow copy of the array for the given field
      const updatedField = [...(prevDetails[field] || [])]
      updatedField.splice(index, 1) // Remove the item at the specified index
      return { ...prevDetails, [field]: updatedField }
    })
  }

  const addNewItem = <T extends keyof DocumentData>(
    field: T,
    defaultValue: DocumentData[T][number]
  ) => {
    setDocumentDetails((prevDetails) => {
      // Create a shallow copy of the array for the given field
      const updatedField = [...(prevDetails[field] || []), defaultValue]
      return { ...prevDetails, [field]: updatedField }
    })
  }

  const handleDateTimeChange = (index: number, date: Dayjs | null, time: Dayjs | null) => {
    if (date && time) {
      const formattedDateTime = `${date.format('h A')} UTC, ${date.format('MMM Do YYYY')}`
      handleFieldChange('timeLines', index, 'time', formattedDateTime)
    } else {
      handleFieldChange('timeLines', index, 'time', '')
    }
  }

  const handleUpdateDocument = async () => {
    try {
      await AppAdmin.documentCreate({
        code: code,
        documentDetail: documentDetails.documentDetail,
        financial: documentDetails.financial,
        timeLines: documentDetails.timeLines,
      })
      message.success('Document updated successfully!')
      setDocumentDetails({
        timeLines: [],
        documentDetail: [],
        financial: [],
      })
      // formDocumentDetail.resetFields();
      getDocumentDetails()
    } catch (error: any) {
      setDocumentDetails({
        timeLines: [],
        documentDetail: [],
        financial: [],
      })
      formDocumentDetail.resetFields()
      message.error(error?.message || 'Error while submitting form')
      logError('Error while submitting form:', error)
    }
  }

  const handleSubmit = () => {
    handleUpdateDocument()
    handleCancel()
  }

  const [infoContract, setInfoContract] = useState<any>({})
  const [showInfo, setShowInfo] = useState(false)

  const getInfoContract = async (contractAddress: string) => {
    try {
      if (contractAddress) {
        const res = await AppAdmin.getInfoContract(contractAddress)
        if (res?.data.success) {
          setInfoContract(res?.data.data)
          message.success('Get contract address info success!')
        }
      }
    } catch (error: any) {
      message.error(error?.message || 'Contract address invalid')
      setInfoContract({})
      logError('Error: ', error)
    }
  }

  useEffect(() => {
    getDocumentDetails()
    handleGetP2PDetails()
  }, [getDocumentDetails, handleGetP2PDetails])

  const [tab, setTab] = useState(0)
  const [latitude, setLatitude] = useState(10.762622)
  const [longitude, setLongitude] = useState(106.660172)

  const [switchChecked, setSwitchChecked] = useState(false)

  const handleSwitchClick = () => {
    Modal.confirm({
      title: 'Are you sure you want to change the status?',
      content:
        'Toggle the switch to manage your P2P lending status. When the switch is ON, the lending is active. When the switch is OFF, the lending is inactive.',
      onOk: () => {
        setSwitchChecked(!switchChecked) // Toggle the switch state
      },
      okText: 'Yes',
      cancelText: 'No',
      centered: true,
    })
  }

  const [, setCenter] = useState<any>({ lat: 10.8231, lng: 106.6297 })

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyDrgqN55Yu90gPGHJbbaWIDvctWYo86cEc',
  })

  if (!isLoaded) return <div>Loading...</div>

  return (
    <div className="main-section p2p-detail">
      <Breadcrumb
        items={[
          {
            href: '/',
            title: <HomeOutlined />,
          },
          {
            title: <span>P2P Detail</span>,
          },
        ]}
        style={{ marginBottom: '20px' }}
      />
      <List
        grid={{ gutter: 16, column: 4 }}
        dataSource={detail}
        className="detail-card"
        renderItem={(item) => (
          <>
            <List.Item>
              {
                <Card className={'card-style'}>
                  <div className={'content-style'}>
                    <div>{item.icon}</div>
                    {!loading ? (
                      <div>
                        <div className={'title-style'}>{item.title}</div>
                        <div className={'value-style'}>{item.value}</div>
                      </div>
                    ) : (
                      <Spin />
                    )}
                  </div>
                </Card>
              }
            </List.Item>
          </>
        )}
      />
      <div className="tab-group">
        <p
          className={tab === 0 ? 'selected' : ''}
          onClick={() => {
            setTab(0)
            setEdit(false)
            handleGetP2PDetails()
          }}
        >
          Details
        </p>
        <p
          className={tab === 1 ? 'selected' : ''}
          onClick={() => {
            setTab(1)
            setEdit(false)
            getDocumentDetails()
            setShowLocation(false)
          }}
        >
          Document
        </p>
        <p
          className={tab === 2 ? 'selected' : ''}
          onClick={() => {
            setTab(2)
            setShowLocation(false)
            getDataTable(1, 10)
            setPageSize(10)
            setCurrent(1)
          }}
        >
          Transaction
        </p>
      </div>
      {tab === 2 && (
        <>
          <Card className="formSearch">
            <Form layout="inline" style={{ padding: 20, justifyContent: 'start' }}>
              <Form.Item>
                <Input
                  allowClear
                  type="text"
                  placeholder="By address"
                  value={addressSearch}
                  onChange={(e) => {
                    setAddressSearch(e.target.value)
                  }}
                />
              </Form.Item>
              <Form.Item>
                <Select
                  allowClear
                  placeholder="By type"
                  options={[
                    { value: HISTORY_TYPE.ALL, label: 'ALL' },
                    { value: HISTORY_TYPE.P2P_DEPOSIT, label: 'P2P DEPOSIT' },
                    { value: HISTORY_TYPE.P2P_WITHDRAW, label: 'P2P WITHDRAW' },
                  ]}
                  value={type}
                  onChange={(value) => setType(value)}
                  style={{ width: '200px', textAlign: 'start' }}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => {
                    setCurrent(1)
                    setPageSize(10)
                    getDataTable(1, 10)
                    getSearchData()
                  }}
                >
                  Search
                </Button>
              </Form.Item>
            </Form>
            {!notFound && (
              <List
                grid={{ gutter: 16, column: 3 }}
                dataSource={[
                  {
                    title: 'Total Balance',
                    Value:
                      Number(searchData.lenderTotalBalance) === 0
                        ? 0
                        : formatNumber(
                            web3.utils.fromWei(searchData.lenderTotalBalance, 'ether'),
                            6
                          ).toString() + ' MEY',
                  },
                  {
                    title: 'Last Claim Time',
                    Value: new Date(Number(searchData.lastClaimTime) * 1000).toLocaleString(),
                  },
                ]}
                className="pool-detail-table-have-contract"
                renderItem={(item) => (
                  <>
                    <List.Item>
                      <Descriptions
                        title=" "
                        layout="vertical"
                        size="small"
                        colon={false}
                        style={{ marginBottom: 20, marginTop: 0 }}
                      >
                        <Descriptions.Item label={item.title}>{item.Value}</Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  </>
                )}
              />
            )}
          </Card>
          <div className="p2p-detail-table">
            <Card style={{ margin: '0 auto' }}>
              <Table<TransactionData>
                columns={columns}
                dataSource={dataTable}
                loading={loading}
                pagination={total < 11 ? false : { total, current, pageSize }}
                onChange={handleTableChange}
                scroll={{ x: 1000 }}
                bordered
              />
            </Card>
          </div>
        </>
      )}
      {tab === 0 && (
        <div className="p2p-create">
          <Form
            form={form}
            layout="vertical"
            initialValues={{ remember: true }}
            onFinish={handleOk}
            // style={{ padding: "20px 10px" }}
          >
            <Col style={{ display: 'none' }} span={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: 'Please input the code!' }]}
              >
                <Input disabled placeholder="Enter the code" />
              </Form.Item>
            </Col>
            <div style={{ textAlign: 'start', paddingBottom: 10 }}>
              {!edit ? (
                <Button
                  style={{ marginRight: 15, width: 100 }}
                  key="edit"
                  onClick={() => setEdit(true)}
                  type="primary"
                >
                  {documentDetails.documentDetail.length < 1 &&
                  documentDetails.financial.length < 1 &&
                  documentDetails.timeLines.length < 1
                    ? 'Create'
                    : 'Edit'}
                </Button>
              ) : (
                <>
                  <Button style={{ marginRight: 15, width: 100 }} key="back" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    style={{ width: 100 }}
                    disabled={thumbnail.length === 0 || thumbnails.length === 0}
                    key="submit"
                    type="primary"
                    htmlType="submit"
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
            <fieldset>
              <legend>Basic Information</legend>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <>
                        Name
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending name, please enter your chosen name as text
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="name"
                    rules={[
                      { required: true, message: 'Please input the name!' },
                      {
                        min: 3,
                        max: 50,
                        message: 'Name must be between 3 and 50 characters!',
                      },
                    ]}
                  >
                    <Input
                      style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                      disabled={!edit}
                      placeholder="Enter the name"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <>
                        Status
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                Toggle the switch to manage your P2P lending status. When the switch
                                is <strong>ON</strong>, the lending is active. When the switch is{' '}
                                <strong>OFF</strong>, the lending is inactive.
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="status"
                    required
                  >
                    <Switch checked={switchChecked} disabled={!edit} onClick={handleSwitchClick} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="country"
                    label={
                      <>
                        Country
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending country, please select your chosen country
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    required
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Select a country"
                      disabled={!edit}
                      style={{ color: 'rgba(0, 0, 0, 0.85) !important' }}
                    >
                      {countries.map((country) => (
                        <Select.Option key={country.code} value={country.code}>
                          <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{country.name}</span>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    style={{ display: 'none' }}
                    label={
                      <>
                        Investment Period
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending investment period, please enter your chosen
                                investment period as number
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="investmentPeriod"
                    rules={[
                      {
                        required: true,
                        message: 'Please input the investment period!',
                      },
                    ]}
                  >
                    <Input type="number" placeholder="Enter the period in months" />
                  </Form.Item>
                </Col>
                <Col style={{ marginBottom: 12 }} span={12}>
                  <Form.Item
                    label={
                      <>
                        Pool Contract Address
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your p2p lending contract address, please enter the correct
                                pool contract address and check its information
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="contractAddress"
                    rules={[
                      {
                        required: true,
                        message: 'Please input the contract address!',
                      },
                      // {
                      //   pattern: /^0x[a-fA-F0-9]{40}$/,
                      //   message: "Please enter a valid contract address starting with 0x followed by 40 hexadecimal characters.",
                      // }
                    ]}
                    style={{ marginBottom: 12 }}
                  >
                    <Input
                      style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                      disabled={!edit}
                      placeholder="Enter the contract address"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Button
                    style={{ marginTop: 30 }}
                    htmlType="button"
                    onClick={() => {
                      if (showInfo) {
                        setShowInfo(!showInfo)
                      } else {
                        setShowInfo(!showInfo)
                        getInfoContract(form.getFieldValue('contractAddress'))
                      }
                    }}
                  >
                    {!showInfo ? 'View Info' : 'Hide Info'}
                  </Button>
                </Col>
                <Col span={24}>
                  {infoContract && showInfo && (
                    <div className="info-contract">
                      <Title style={{ marginTop: 0 }} level={4}>
                        Contract Information
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending address information, check the data below
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <InfoCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </Title>
                      <Descriptions bordered column={1} style={{ marginBottom: '16px' }}>
                        <Descriptions.Item label="APY">{infoContract.apy}</Descriptions.Item>
                        <Descriptions.Item label="Boots APY">
                          {infoContract.boots_apy}
                        </Descriptions.Item>
                        <Descriptions.Item label="Funding Target">
                          {infoContract.fundingTarget}
                        </Descriptions.Item>
                        <Descriptions.Item label="Start Time">
                          {infoContract.startDate}
                        </Descriptions.Item>
                        <Descriptions.Item label="End Time">
                          {infoContract.endDate}
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  )}
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={
                      <>
                        Description
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending description, please enter your chosen
                                description as text
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="description"
                    rules={[
                      { required: true, message: 'Please input the description!' },
                      {
                        min: 3,
                        max: 500,
                        message: 'Description must be between 3 and 500 characters!',
                      },
                    ]}
                  >
                    <Input.TextArea
                      style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                      disabled={!edit}
                      rows={4}
                      placeholder="Enter the description"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </fieldset>
            <fieldset>
              <legend>Property Information</legend>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <>
                        Property Type
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending property type. Please select your chosen
                                property. After selecting a property, a map will appear for you to
                                pick the location
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="type"
                    rules={[{ required: true, message: 'Please select the type!' }]}
                  >
                    <Select
                      disabled={!edit}
                      placeholder="Select property type"
                      onChange={handlePropertyTypeChange}
                    >
                      {propertyTypes.map((type) => (
                        <Select.Option key={type.id} value={type.id}>
                          {type.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {/* {showLocation && (
                  <Col
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 40,
                      marginBottom: 40,
                    }}
                    span={24}
                  >
                    <div className={!edit ? 'map-disabled' : ''} style={{ height: "300px", width: "500px" }}>
                      <GoogleMap
                        mapContainerStyle={{ width: '500px', height: '300px' }}
                        center={center}
                        zoom={zoom}
                        onClick={(e) => {
                          const { latLng } = e;
                          setCenter({ lat: latLng?.lat(), lng: latLng?.lng() });
                          handleChangeLocation(latLng?.lat(), latLng?.lng());
                        }}
                      >
                        <Marker position={center} />
                      </GoogleMap>
                    </div>
                  </Col>
                )} */}
                <Col span={12}>
                  <Form.Item
                    label={
                      <>
                        Latitude
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending location coordinates (latitude). You can
                                get the value by entering your chosen latitude as number
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="latitude"
                  >
                    <Input
                      disabled={!edit}
                      style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                      type="number"
                      step={'any'}
                      min={-180}
                      max={180}
                      placeholder="Enter latitude"
                      value={latitude}
                      onChange={(e) => {
                        setLatitude(parseFloat(e.target.value))
                        setCenter({ lat: parseFloat(e.target.value), lng: longitude })
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={
                      <>
                        Longitude
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending location coordinates (longitude). You can
                                get the value by entering your chosen longitude as number
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    name="longitude"
                  >
                    <Input
                      disabled={!edit}
                      style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                      type="number"
                      step={'any'}
                      min={-180}
                      max={180}
                      placeholder="Enter longitude"
                      value={longitude}
                      onChange={(e) => {
                        setLongitude(parseFloat(e.target.value))
                        setCenter({ lat: latitude, lng: parseFloat(e.target.value) })
                      }}
                    />
                  </Form.Item>
                </Col>
                {/* <Button onClick={() => setDefaultLocation({lat: latitude, lng: longitude})}>Test</Button> */}
                {propertyFields.map((field) => {
                  const isNumberField =
                    field.name === 'Area' ||
                    field.name === 'Property Value' ||
                    field.name === 'Price Per SQM' ||
                    field.name === 'Beds' ||
                    field.name === 'Baths' ||
                    field.name === 'Year Built' ||
                    field.name === 'Floors' ||
                    field.name === 'Annual Gross Rental Yield' ||
                    field.name === 'Annual Asset Appreciation' ||
                    field.name === 'Total Projected Gross Yield'

                  return (
                    <Col span={12} key={field.key}>
                      <Form.Item
                        name={`property_${field.key}`}
                        label={
                          <>
                            {field.name}
                            <Tooltip
                              title={
                                <div className="tooltip-box">
                                  <p className="tooltip-text">
                                    This is your P2P lending {field.name} property, please enter
                                    your chosen {field.name} as {isNumberField ? 'number' : 'text'}
                                  </p>
                                </div>
                              }
                              placement="right"
                              overlayInnerStyle={{
                                backgroundColor: '#ffffff',
                              }}
                            >
                              <QuestionCircleOutlined className="tooltip-icon" />
                            </Tooltip>
                          </>
                        }
                      >
                        {isNumberField ? (
                          <InputNumber
                            disabled={!edit}
                            style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
                            addonAfter={
                              field.name === 'Annual Gross Rental Yield' ||
                              field.name === 'Annual Asset Appreciation' ||
                              field.name === 'Total Projected Gross Yield'
                                ? '%'
                                : field.name === 'Property Value' || field.name === 'Price Per SQM'
                                  ? '$'
                                  : field.name === 'Area'
                                    ? `m\u00B2`
                                    : undefined
                            }
                            min={0}
                            step={1}
                            max={
                              field.name === 'Annual Gross Rental Yield' ||
                              field.name === 'Annual Asset Appreciation' ||
                              field.name === 'Total Projected Gross Yield'
                                ? 100
                                : undefined
                            }
                            formatter={(value: any) =>
                              field.name === 'Beds' ||
                              field.name === 'Baths' ||
                              field.name === 'Year Built' ||
                              field.name === 'Floors'
                                ? value
                                : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            parser={(value: any) =>
                              field.name === 'Beds' ||
                              field.name === 'Baths' ||
                              field.name === 'Year Built' ||
                              field.name === 'Floors'
                                ? value
                                : (value?.replace(/\$\s?|(,*)/g, '') as unknown as number)
                            }
                            value={Number(field.value)}
                            onChange={(value: any) => {
                              const newFields = [...propertyFields]
                              const index = newFields.findIndex((f) => f.key === field.key)
                              newFields[index].value = value
                              setPropertyFields(newFields)
                            }}
                            placeholder={`Enter ${field.name}`}
                          />
                        ) : (
                          <Input
                            disabled={!edit}
                            style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                            value={field.value}
                            // addonAfter={
                            //   field.name === "Annual Gross Rental Yield" ||
                            //     field.name === "Annual Asset Appreciation" ||
                            //     field.name === "Total Projected Gross Yield"
                            //     ? "%"
                            //     : field.name === "Property Value"
                            //       ? "$"
                            //       : field.name === "Area"
                            //         ? `m\u00B2`
                            //         : undefined
                            // }
                            onChange={(e) => {
                              const newFields = [...propertyFields]
                              const index = newFields.findIndex((f) => f.key === field.key)
                              newFields[index].value = e.target.value
                              setPropertyFields(newFields)
                            }}
                            placeholder={`Enter ${field.name}`}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  )
                })}
              </Row>
            </fieldset>
            <fieldset>
              <legend>Upload Picture</legend>
              <Row gutter={24}>
                <Col span={12}>
                  {thumbnail.length > 0 && (
                    <>
                      <img style={{ width: '50%', border: '1px solid' }} src={thumbnail} alt="" />
                      <CloseOutlined
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: '50%',
                          color: '#000',
                          fontSize: '16px',
                          cursor: 'pointer',
                          background: 'white',
                          borderRadius: '50%',
                          padding: '2px',
                          border: '1px solid',
                          display: edit ? 'unset' : 'none',
                        }}
                        onClick={() => {
                          setThumbnail('')
                          setSingleFile([])
                        }}
                      />
                    </>
                  )}
                </Col>
                <Col span={12}>
                  <Row gutter={24}>
                    {thumbnails.length > 0 &&
                      thumbnails.map((item, index) => (
                        <Col span={8} key={index} style={{ position: 'relative' }}>
                          <img style={{ width: '100%', border: '1px solid' }} src={item} alt="" />
                          <CloseOutlined
                            style={{
                              position: 'absolute',
                              top: 4,
                              right: 14,
                              color: '#000',
                              fontSize: '16px',
                              cursor: 'pointer',
                              background: 'white',
                              borderRadius: '50%',
                              padding: '2px',
                              border: '1px solid',
                              display: edit ? 'unset' : 'none',
                            }}
                            onClick={() => handleRemoveImage(index)}
                          />
                        </Col>
                      ))}
                  </Row>
                </Col>
                {edit && (
                  <>
                    <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ color: 'red' }}>*</span> Upload new avatar{' '}
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending avatar. Please upload your chosen image.
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </div>
                      <Form.Item name="uploadSingle">
                        <Upload
                          disabled={!edit}
                          fileList={singleFile}
                          customRequest={handleUploadSingle}
                          beforeUpload={beforeUploadSingle}
                        >
                          <Button icon={<UploadOutlined />}>Upload Single File</Button>
                        </Upload>
                      </Form.Item>
                      <p>You can only upload JPG/PNG file.</p>
                      <p>The image should have the size of 858x423 px.</p>
                    </Col>
                    <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ color: 'red' }}>*</span> Upload new thumbnails{' '}
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is your P2P lending background thumbnails list. Please upload
                                your chosen images.
                              </p>
                            </div>
                          }
                          placement="right"
                          overlayInnerStyle={{
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </div>
                      <Form.Item name="upLloadMulti">
                        <Upload
                          listType="text"
                          beforeUpload={beforeUploadMulti}
                          {...props}
                          disabled={!edit}
                        >
                          <Button icon={<UploadOutlined />}>Upload Multiple Files</Button>
                        </Upload>
                        <Button
                          type="primary"
                          onClick={handleUploadMulti}
                          disabled={fileList.length === 0}
                          style={{ marginTop: 16 }}
                        >
                          Start Upload
                        </Button>
                      </Form.Item>

                      <p>You can only upload JPG/PNG file.</p>
                      <p>The image should have the size of 1024x500 px.</p>
                    </Col>
                  </>
                )}
              </Row>
            </fieldset>
          </Form>
        </div>
      )}
      {tab === 1 && (
        <div className="p2p-create">
          <Form onFinish={handleSubmit} form={formDocumentDetail} layout="vertical">
            <div style={{ textAlign: 'start', paddingBottom: 10 }}>
              {!edit ? (
                <Button
                  style={{ marginRight: 15, width: 100 }}
                  key="edit"
                  onClick={() => setEdit(true)}
                  type="primary"
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button style={{ marginRight: 15, width: 100 }} key="back" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    style={{ width: 100 }}
                    disabled={
                      !canEdit

                      //   ||
                      // documentDetails.documentDetail.length < 1 ||
                      // documentDetails.financial.length < 1 ||
                      // documentDetails.timeLines.length < 1
                    }
                    key="submit"
                    type="primary"
                    htmlType="submit"
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
            <fieldset>
              <legend>Timeline</legend>
              <List
                dataSource={documentDetails.timeLines}
                renderItem={(item, index) => (
                  <List.Item
                    actions={[
                      <Popconfirm
                        key={item.title}
                        title="Are you sure to delete this time line?"
                        onConfirm={() => handleDeleteItem('timeLines', index)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <DeleteOutlined
                          style={
                            edit
                              ? {}
                              : {
                                  pointerEvents: 'none',
                                  cursor: 'not-allowed',
                                }
                          }
                          key="delete"
                        />
                      </Popconfirm>,
                    ]}
                  >
                    <Row gutter={16} style={{ width: '100%' }}>
                      <Col span={12}>
                        <p className="document-title">
                          Date Picker{' '}
                          <Tooltip
                            title={
                              <div className="tooltip-box">
                                <p className="tooltip-text">
                                  This is the P2P Process Timeline, please select the time using the
                                  date-time picker
                                </p>
                              </div>
                            }
                            placement="right"
                            overlayInnerStyle={{
                              backgroundColor: '#ffffff',
                            }}
                          >
                            <QuestionCircleOutlined className="tooltip-icon" />
                          </Tooltip>
                        </p>
                        <DatePicker
                          disabled={!edit}
                          style={{ width: '100%' }}
                          showTime
                          placeholder="Select date"
                          defaultValue={dayjs()}
                          // value={dayjs.utc(item.time, "h A UTC, MMM Do YYYY")}
                          format={'h A UTC, MMM Do YYYY'}
                          onOk={(date) =>
                            handleDateTimeChange(
                              index,
                              date,
                              dayjs(item.time, 'h A UTC, MMM Do YYYY')
                            )
                          }
                        />
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={
                            <>
                              Date Selected{' '}
                              <Tooltip
                                title={
                                  <div className="tooltip-box">
                                    <p className="tooltip-text">This is the formatted date</p>
                                  </div>
                                }
                                placement="right"
                                overlayInnerStyle={{
                                  backgroundColor: '#ffffff',
                                }}
                              >
                                <QuestionCircleOutlined className="tooltip-icon" />
                              </Tooltip>
                            </>
                          }
                        >
                          <Input
                            disabled
                            value={item.time}
                            placeholder="Formatted date-time"
                            style={{
                              backgroundColor: '#d9d9d9',
                              cursor: 'not-allowed',
                              opacity: 1,
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12} style={{ marginTop: 8 }}>
                        <Form.Item
                          label={
                            <>
                              Title{' '}
                              <Tooltip
                                title={
                                  <div className="tooltip-box">
                                    <p className="tooltip-text">
                                      This is the P2P Process Timeline title. Please enter your
                                      chosen title as text.
                                    </p>
                                  </div>
                                }
                                placement="right"
                                overlayInnerStyle={{
                                  backgroundColor: '#ffffff',
                                }}
                              >
                                <QuestionCircleOutlined className="tooltip-icon" />
                              </Tooltip>
                            </>
                          }
                          // name={["timeLines", index, "title"]}
                          rules={[
                            { required: true, message: 'Please enter a title' },
                            {
                              min: 3,
                              max: 50,
                              message: 'Input must be between 3 and 50 characters',
                            },
                          ]}
                        >
                          <Input
                            style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                            disabled={!edit}
                            value={item.title}
                            placeholder="Enter title"
                            onChange={(e) =>
                              handleFieldChange('timeLines', index, 'title', e.target.value)
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12} style={{ marginTop: 8 }}>
                        <Form.Item
                          label={
                            <>
                              Description{' '}
                              <Tooltip
                                title={
                                  <div className="tooltip-box">
                                    <p className="tooltip-text">
                                      This is the P2P Process Timeline title. Please enter your
                                      chosen title as text. Special characters are not allowed; only
                                      letters, numbers, and spaces are accepted
                                    </p>
                                  </div>
                                }
                                placement="right"
                                overlayInnerStyle={{
                                  backgroundColor: '#ffffff',
                                }}
                              >
                                <QuestionCircleOutlined className="tooltip-icon" />
                              </Tooltip>
                            </>
                          }
                          // name={["timeLines", index, "desc"]}
                          rules={[
                            { required: true, message: 'Please enter a description' },
                            {
                              min: 3,
                              max: 50,
                              message: 'Input must be between 3 and 50 characters',
                            },
                          ]}
                        >
                          <Input
                            style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                            disabled={!edit}
                            value={item.desc}
                            placeholder="Enter description"
                            onChange={(e) =>
                              handleFieldChange('timeLines', index, 'desc', e.target.value)
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </List.Item>
                )}
              />
              <Button
                type="dashed"
                onClick={() => addNewItem('timeLines', { time: '', title: '', desc: '' })}
                style={{ width: '100%', marginBottom: 16 }}
                disabled={!canEdit || !edit}
              >
                <PlusOutlined /> Add Time Line
              </Button>
            </fieldset>
            <fieldset>
              <legend>Document Details</legend>
              <>
                <List
                  dataSource={documentDetails.documentDetail}
                  renderItem={(doc, index) => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          key={`${doc.name}-${index}`}
                          title="Are you sure to delete this document?"
                          onConfirm={() => handleDeleteItem('documentDetail', index)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <DeleteOutlined
                            style={
                              edit
                                ? {}
                                : {
                                    pointerEvents: 'none',
                                    cursor: 'not-allowed',
                                  }
                            }
                            key="delete"
                          />
                        </Popconfirm>,
                      ]}
                    >
                      <Row gutter={16} style={{ width: '100%' }}>
                        <Col span={12}>
                          <Form.Item
                            label={
                              <>
                                Name{' '}
                                <Tooltip
                                  title={
                                    <div className="tooltip-box">
                                      <p className="tooltip-text">
                                        This is the Document name. Please enter your chosen name as
                                        text.
                                      </p>
                                    </div>
                                  }
                                  placement="right"
                                  overlayInnerStyle={{
                                    backgroundColor: '#ffffff',
                                  }}
                                >
                                  <QuestionCircleOutlined className="tooltip-icon" />
                                </Tooltip>
                              </>
                            }
                            // name={["documentDetail", index, "name"]}
                            rules={[
                              { required: true, message: 'Please enter a document name' },
                              {
                                min: 3,
                                max: 50,
                                message: 'Input must be between 3 and 50 characters',
                              },
                            ]}
                          >
                            <Input
                              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                              disabled={!edit}
                              value={doc.name}
                              placeholder="Enter document name"
                              onChange={(e) =>
                                handleFieldChange('documentDetail', index, 'name', e.target.value)
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={
                              <>
                                Link{' '}
                                <Tooltip
                                  title={
                                    <div className="tooltip-box">
                                      <p className="tooltip-text">
                                        This is the document link. Please enter your chosen link in
                                        text link format
                                      </p>
                                    </div>
                                  }
                                  placement="right"
                                  overlayInnerStyle={{
                                    backgroundColor: '#ffffff',
                                  }}
                                >
                                  <QuestionCircleOutlined className="tooltip-icon" />
                                </Tooltip>
                              </>
                            }
                            // name={["documentDetail", index, "link"]}
                            rules={[
                              { required: true, message: 'Please enter a document link' },
                              { type: 'url', message: 'Please enter a valid URL' },
                            ]}
                          >
                            <Input
                              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                              disabled={!edit}
                              value={doc.link}
                              placeholder="Enter document link"
                              onChange={(e) =>
                                handleFieldChange('documentDetail', index, 'link', e.target.value)
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </List.Item>
                  )}
                />
                <Button
                  type="dashed"
                  onClick={() => addNewItem('documentDetail', { name: '', link: '' })}
                  style={{ width: '100%', marginBottom: 16 }}
                  disabled={!canEdit || !edit}
                >
                  <PlusOutlined /> Add Document
                </Button>
              </>
            </fieldset>
            <fieldset>
              <legend>Financial Details</legend>
              <>
                <List
                  dataSource={documentDetails.financial}
                  renderItem={(fin, index) => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          key={`${fin.value}-${index}`}
                          title="Are you sure to delete this financial item?"
                          onConfirm={() => handleDeleteItem('financial', index)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <DeleteOutlined
                            style={
                              edit
                                ? {}
                                : {
                                    pointerEvents: 'none',
                                    cursor: 'not-allowed',
                                  }
                            }
                            key="delete"
                          />
                        </Popconfirm>,
                      ]}
                    >
                      <Row gutter={16} style={{ width: '100%' }}>
                        {/* <Col span={12}>
                          <p className="document-title">Name <Tooltip
                            title={
                              <div className="tooltip-box">
                                <p className="tooltip-text">
                                  This is the Financial name. Please enter your chosen
                                  name as text. Special characters are not allowed;
                                  only letters, numbers, and spaces are accepted
                                </p>
                              </div>
                            }
                            placement="right"
                            overlayInnerStyle={{
                              backgroundColor: "#ffffff",
                            }}
                          >
                            <QuestionCircleOutlined className="tooltip-icon" />
                          </Tooltip>
                          </p>
                          <Input
                            value={fin.name}
                            placeholder="Enter financial name"
                            onChange={(e) =>
                              handleFieldChange(
                                "financial",
                                index,
                                "name",
                                e.target.value
                              )
                            }
                          />
                        </Col>
                        <Col span={12}>
                          <p className="document-title">Value <Tooltip
                            title={
                              <div className="tooltip-box">
                                <p className="tooltip-text">
                                  This is the Financial value. Please enter your
                                  chosen value as text. Special characters are not
                                  allowed; only letters, numbers, and spaces are
                                  accepted
                                </p>
                              </div>
                            }
                            placement="right"
                            overlayInnerStyle={{
                              backgroundColor: "#ffffff",
                            }}
                          >
                            <QuestionCircleOutlined className="tooltip-icon" />
                          </Tooltip>
                          </p>
                          <Input
                            value={fin.value}
                            placeholder="Enter financial value"
                            onChange={(e) =>
                              handleFieldChange(
                                "financial",
                                index,
                                "value",
                                e.target.value
                              )
                            }
                          />
                        </Col> */}
                        <Col span={12}>
                          <Form.Item
                            label={
                              <>
                                Name{' '}
                                <Tooltip
                                  title={
                                    <div className="tooltip-box">
                                      <p className="tooltip-text">
                                        This is the Financial name. Please enter your chosen name as
                                        text. Special characters are not allowed; only letters,
                                        numbers, and spaces are accepted
                                      </p>
                                    </div>
                                  }
                                  placement="right"
                                  overlayInnerStyle={{
                                    backgroundColor: '#ffffff',
                                  }}
                                >
                                  <QuestionCircleOutlined className="tooltip-icon" />
                                </Tooltip>
                              </>
                            }
                            // name={["financial", index, "name"]}
                            rules={[
                              { required: true, message: 'Please enter a financial name' },
                              {
                                min: 3,
                                max: 50,
                                message: 'Input must be between 3 and 50 characters',
                              },
                            ]}
                          >
                            <Input
                              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                              disabled={!edit}
                              value={fin.name}
                              placeholder="Enter financial name"
                              onChange={(e) =>
                                handleFieldChange('financial', index, 'name', e.target.value)
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={
                              <>
                                Value{' '}
                                <Tooltip
                                  title={
                                    <div className="tooltip-box">
                                      <p className="tooltip-text">
                                        This is the Financial value. Please enter your chosen value
                                        as text. Special characters are not allowed; only letters,
                                        numbers, and spaces are accepted
                                      </p>
                                    </div>
                                  }
                                  placement="right"
                                  overlayInnerStyle={{
                                    backgroundColor: '#ffffff',
                                  }}
                                >
                                  <QuestionCircleOutlined className="tooltip-icon" />
                                </Tooltip>
                              </>
                            }
                            // name={["financial", index, "value"]}
                            rules={[
                              { required: true, message: 'Please enter a financial name' },
                              // {
                              //   min: 3,
                              //   max: 50,
                              //   message: 'Input must be between 3 and 50 characters',
                              // },
                            ]}
                          >
                            <Input
                              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                              disabled={!edit}
                              value={fin.value}
                              placeholder="Enter financial value"
                              onChange={(e) =>
                                handleFieldChange('financial', index, 'value', e.target.value)
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </List.Item>
                  )}
                />
                <Button
                  type="dashed"
                  onClick={() => addNewItem('financial', { name: '', value: '' })}
                  style={{ width: '100%', marginBottom: 16 }}
                  disabled={!canEdit || !edit}
                >
                  <PlusOutlined /> Add Financial Item
                </Button>
              </>
            </fieldset>
          </Form>
        </div>
      )}
    </div>
  )
}
